<template>
  <Teleport to="body">
    <Transition name="auth-modal" @enter="onEnter" @leave="onLeave" :css="false">
      <div
        v-if="isOpen"
        class="fixed inset-0 z-[60] flex items-center justify-center p-0 md:p-6"
        @click.self="$emit('close')"
      >
        <!-- Backdrop -->
        <div
          ref="backdropRef"
          class="fixed inset-0 bg-black/50 backdrop-blur-sm"
          aria-hidden="true"
          @click="$emit('close')"
        />

        <!-- Panel -->
        <div
          ref="panelRef"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="titleId"
          tabindex="-1"
          class="auth-panel relative w-full md:max-w-md h-full md:h-auto max-h-dvh md:max-h-[90vh] outline-none"
        >
          <div class="auth-card bg-white flex flex-col h-full md:h-auto overflow-hidden md:rounded-[28px] shadow-[0_20px_60px_-15px_var(--shadow-color-heavy)] border border-black/5">
            <!-- Header -->
            <div class="relative px-6 pt-6 pb-3 shrink-0">
              <button
                type="button"
                @click="$emit('close')"
                class="absolute right-4 top-4 h-9 w-9 grid place-items-center rounded-full bg-muted/60 hover:bg-muted text-foreground/70 hover:text-foreground transition-all duration-150 active:scale-90"
                aria-label="Close"
              >
                <Icon name="tabler:x" size="18" />
              </button>

              <h2 :id="titleId" class="text-xl md:text-2xl font-bold tracking-tight text-foreground pr-10">
                {{ $t('auth.modal.title') }}
              </h2>

              <!-- Pill tabs -->
              <div class="relative mt-5 p-1 bg-muted/70 rounded-full flex" ref="tabsRef">
                <span
                  ref="tabIndicatorRef"
                  class="absolute top-1 bottom-1 left-1 rounded-full bg-white shadow-[0_2px_8px_var(--shadow-color-md)] pointer-events-none"
                  style="width: calc(50% - 4px);"
                />
                <button
                  type="button"
                  @click="setTab('login')"
                  :class="[
                    'relative flex-1 py-2.5 text-sm font-semibold rounded-full transition-colors duration-200',
                    activeTab === 'login' ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
                  ]"
                >
                  {{ $t('auth.login.tab') }}
                </button>
                <button
                  type="button"
                  @click="setTab('register')"
                  :class="[
                    'relative flex-1 py-2.5 text-sm font-semibold rounded-full transition-colors duration-200',
                    activeTab === 'register' ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
                  ]"
                >
                  {{ $t('auth.register.tab') }}
                </button>
              </div>
            </div>

            <!-- Body -->
            <div class="px-6 pb-6 pt-4 flex-1 overflow-y-auto">
              <div ref="formsRef" class="relative">
                <!-- Login Form -->
                <VeeForm
                  v-if="activeTab === 'login'"
                  :key="'login'"
                  :validation-schema="loginSchema"
                  @submit="handleLogin"
                  class="space-y-4 auth-form"
                  v-slot="{ meta }"
                >
                  <UiAlert v-if="serverError" variant="destructive" class="rounded-2xl">
                    <Icon name="tabler:alert-circle" size="16" />
                    <UiAlertDescription>{{ serverError }}</UiAlertDescription>
                  </UiAlert>

                  <div class="auth-row space-y-1.5">
                    <label for="login-email" class="block text-sm font-semibold text-foreground/80 pl-1">
                      {{ $t('auth.fields.email') }}
                    </label>
                    <VeeField name="email" v-slot="{ field, errorMessage, meta: fieldMeta }">
                      <div :class="['auth-input-wrap', fieldMeta.touched && errorMessage ? 'is-invalid' : '']">
                        <UiInput
                          id="login-email"
                          v-bind="field"
                          type="email"
                          :placeholder="$ts('auth.fields.emailPlaceholder')"
                          autocomplete="email"
                          class="auth-input"
                        />
                      </div>
                      <Transition name="auth-error">
                        <p v-if="fieldMeta.touched && errorMessage" class="text-xs text-destructive mt-1 pl-1 font-medium">
                          {{ friendlyError(errorMessage, 'email') }}
                        </p>
                      </Transition>
                    </VeeField>
                  </div>

                  <div class="auth-row space-y-1.5">
                    <div class="flex items-center justify-between pl-1 pr-1">
                      <label for="login-password" class="block text-sm font-semibold text-foreground/80">
                        {{ $t('auth.fields.password') }}
                      </label>
                      <NuxtLink
                        :to="$localeRoute('/account/forgot-password')"
                        class="text-xs font-semibold text-primary hover:underline"
                        @click="$emit('close')"
                      >
                        {{ $t('auth.login.forgotPassword') }}
                      </NuxtLink>
                    </div>
                    <VeeField name="password" v-slot="{ field, errorMessage, meta: fieldMeta }">
                      <div :class="['auth-input-wrap', fieldMeta.touched && errorMessage ? 'is-invalid' : '']">
                        <UiInput
                          id="login-password"
                          v-bind="field"
                          type="password"
                          :placeholder="$ts('auth.fields.passwordPlaceholder')"
                          autocomplete="current-password"
                          class="auth-input"
                        />
                      </div>
                      <Transition name="auth-error">
                        <p v-if="fieldMeta.touched && errorMessage" class="text-xs text-destructive mt-1 pl-1 font-medium">
                          {{ friendlyError(errorMessage, 'password') }}
                        </p>
                      </Transition>
                    </VeeField>
                  </div>

                  <button
                    type="submit"
                    :disabled="isLoading || !meta.valid"
                    class="auth-cta w-full"
                  >
                    <Icon v-if="isLoading" name="tabler:loader-2" size="18" class="animate-spin" />
                    <span>{{ $t('auth.login.submit') }}</span>
                  </button>

                  <div class="relative py-1">
                    <div class="absolute inset-0 flex items-center"><span class="w-full border-t border-black/10" /></div>
                    <div class="relative flex justify-center">
                      <span class="bg-white px-3 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
                        {{ $t('auth.login.orContinueWith') }}
                      </span>
                    </div>
                  </div>

                  <div class="grid grid-cols-2 gap-3">
                    <button type="button" @click="handleGoogleLogin" :disabled="isLoading" class="auth-social">
                      <Icon name="devicon:google" size="18" />
                      <span>Google</span>
                    </button>
                    <button type="button" @click="handleAppleLogin" :disabled="isLoading" class="auth-social">
                      <Icon name="devicon:apple" size="18" />
                      <span>Apple</span>
                    </button>
                  </div>

                  <button type="button" @click="handlePasskeyLogin" :disabled="isLoading" class="auth-social w-full">
                    <Icon name="tabler:fingerprint" size="18" />
                    <span>{{ $t('auth.login.passkey') }}</span>
                  </button>
                </VeeForm>

                <!-- Register Form -->
                <VeeForm
                  v-else
                  :key="'register'"
                  :validation-schema="registerSchema"
                  :initial-values="registerInitialValues"
                  @submit="handleRegister"
                  class="space-y-4 auth-form"
                  v-slot="{ meta }"
                >
                  <UiAlert v-if="registerSuccess" variant="success" class="rounded-2xl">
                    <Icon name="tabler:circle-check" size="16" />
                    <UiAlertDescription>{{ $t('auth.register.successMessage') }}</UiAlertDescription>
                  </UiAlert>

                  <UiAlert v-if="serverError" variant="destructive" class="rounded-2xl">
                    <Icon name="tabler:alert-circle" size="16" />
                    <UiAlertDescription>{{ serverError }}</UiAlertDescription>
                  </UiAlert>

                  <template v-if="!registerSuccess">
                    <div class="auth-row space-y-1.5">
                      <label for="register-email" class="block text-sm font-semibold text-foreground/80 pl-1">
                        {{ $t('auth.fields.email') }}
                      </label>
                      <VeeField name="email" v-slot="{ field, errorMessage, meta: fieldMeta }">
                        <div :class="['auth-input-wrap', fieldMeta.touched && errorMessage ? 'is-invalid' : '']">
                          <UiInput
                            id="register-email"
                            v-bind="field"
                            type="email"
                            :placeholder="$ts('auth.fields.emailPlaceholder')"
                            autocomplete="email"
                            class="auth-input"
                          />
                        </div>
                        <Transition name="auth-error">
                          <p v-if="fieldMeta.touched && errorMessage" class="text-xs text-destructive mt-1 pl-1 font-medium">
                            {{ friendlyError(errorMessage, 'email') }}
                          </p>
                        </Transition>
                      </VeeField>
                    </div>

                    <div class="auth-row space-y-1.5">
                      <label for="register-password" class="block text-sm font-semibold text-foreground/80 pl-1">
                        {{ $t('auth.fields.password') }}
                      </label>
                      <VeeField name="password" v-slot="{ field, errorMessage, meta: fieldMeta }">
                        <div :class="['auth-input-wrap', fieldMeta.touched && errorMessage ? 'is-invalid' : '']">
                          <UiInput
                            id="register-password"
                            v-bind="field"
                            type="password"
                            :placeholder="$ts('auth.fields.passwordPlaceholder')"
                            autocomplete="new-password"
                            class="auth-input"
                          />
                        </div>
                        <Transition name="auth-error">
                          <p v-if="fieldMeta.touched && errorMessage" class="text-xs text-destructive mt-1 pl-1 font-medium">
                            {{ friendlyError(errorMessage, 'password') }}
                          </p>
                        </Transition>
                      </VeeField>
                    </div>

                    <div class="auth-row space-y-1.5">
                      <label for="register-confirm" class="block text-sm font-semibold text-foreground/80 pl-1">
                        {{ $t('auth.fields.confirmPassword') }}
                      </label>
                      <VeeField name="confirmPassword" v-slot="{ field, errorMessage, meta: fieldMeta }">
                        <div :class="['auth-input-wrap', fieldMeta.touched && errorMessage ? 'is-invalid' : '']">
                          <UiInput
                            id="register-confirm"
                            v-bind="field"
                            type="password"
                            :placeholder="$ts('auth.fields.confirmPasswordPlaceholder')"
                            autocomplete="new-password"
                            class="auth-input"
                          />
                        </div>
                        <Transition name="auth-error">
                          <p v-if="fieldMeta.touched && errorMessage" class="text-xs text-destructive mt-1 pl-1 font-medium">
                            {{ friendlyError(errorMessage, 'confirmPassword') }}
                          </p>
                        </Transition>
                      </VeeField>
                    </div>

                    <!-- Consent checkboxes -->
                    <div class="auth-row space-y-3 pt-1">
                      <VeeField name="agreeTerms" v-slot="{ errorMessage: termsError, meta: termsMeta }">
                        <label class="flex items-start gap-2.5 cursor-pointer group">
                          <RekaCheckbox
                            :model-value="agreeTerms"
                            class="mt-0.5 auth-check"
                            @update:model-value="(val) => { agreeTerms = Boolean(val); onCheckPop($event); }"
                          />
                          <span class="text-xs leading-relaxed text-muted-foreground">
                            <i18n-t keypath="auth.register.agreeTerms">
                              <template #terms>
                                <NuxtLink :to="$localeRoute('/legal/terms')" class="text-primary font-semibold hover:underline" target="_blank" rel="noopener noreferrer">{{ $t('auth.register.termsLink') }}</NuxtLink>
                              </template>
                              <template #privacy>
                                <NuxtLink :to="$localeRoute('/legal/privacy')" class="text-primary font-semibold hover:underline" target="_blank" rel="noopener noreferrer">{{ $t('auth.register.privacyLink') }}</NuxtLink>
                              </template>
                            </i18n-t>
                          </span>
                        </label>
                        <Transition name="auth-error">
                          <p v-if="termsMeta.touched && termsError" class="text-xs text-destructive mt-1 ml-6 font-medium">{{ termsError }}</p>
                        </Transition>
                      </VeeField>

                      <label class="flex items-start gap-2.5 cursor-pointer">
                        <RekaCheckbox
                          :model-value="agreeMarketing"
                          class="mt-0.5 auth-check"
                          @update:model-value="(val) => { agreeMarketing = Boolean(val); onCheckPop($event); }"
                        />
                        <span class="text-xs leading-relaxed text-muted-foreground">{{ $t('auth.register.agreeMarketing') }}</span>
                      </label>

                      <label class="flex items-start gap-2.5 cursor-pointer">
                        <RekaCheckbox
                          :model-value="agreeAnalytics"
                          class="mt-0.5 auth-check"
                          @update:model-value="(val) => { agreeAnalytics = Boolean(val); onCheckPop($event); }"
                        />
                        <span class="text-xs leading-relaxed text-muted-foreground">{{ $t('auth.register.agreeAnalytics') }}</span>
                      </label>
                    </div>

                    <button
                      type="submit"
                      :disabled="isLoading || !meta.valid || !agreeTerms"
                      class="auth-cta w-full"
                    >
                      <Icon v-if="isLoading" name="tabler:loader-2" size="18" class="animate-spin" />
                      <span>{{ $t('auth.register.submit') }}</span>
                    </button>

                    <div class="relative py-1">
                      <div class="absolute inset-0 flex items-center"><span class="w-full border-t border-black/10" /></div>
                      <div class="relative flex justify-center">
                        <span class="bg-white px-3 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
                          {{ $t('auth.register.orContinueWith') }}
                        </span>
                      </div>
                    </div>

                    <div class="grid grid-cols-2 gap-3">
                      <button type="button" @click="handleGoogleLogin" :disabled="isLoading" class="auth-social">
                        <Icon name="devicon:google" size="18" />
                        <span>Google</span>
                      </button>
                      <button type="button" @click="handleAppleLogin" :disabled="isLoading" class="auth-social">
                        <Icon name="devicon:apple" size="18" />
                        <span>Apple</span>
                      </button>
                    </div>
                  </template>
                </VeeForm>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useId, useI18n, useNuxtApp, useRouter } from '#imports';
import { toTypedSchema } from '@vee-validate/zod';
import { z } from 'zod';
import { gsap } from 'gsap';
import { useAuthClient } from '#composables/useAuthClient';
import { useGoogleOneTap } from '#composables/useGoogleOneTap';
import { useConsentState } from '#bubba/shared/composables/consent/useConsentState';
import { useConvexMutation } from '#composables/useConvex';
import { api } from '@convex/_generated/api';
import { createLogger } from '#bubba/shared/utils/logger';
import { primeSessionCache } from '#bubba/shared/utils/sessionCache';

const log = createLogger('AuthPanel', { enabled: false });

const titleId = useId();
const panelRef = ref<HTMLElement | null>(null);
const backdropRef = ref<HTMLElement | null>(null);
const tabsRef = ref<HTMLElement | null>(null);
const tabIndicatorRef = ref<HTMLElement | null>(null);
const formsRef = ref<HTMLElement | null>(null);

const props = defineProps<{
  isOpen: boolean;
  initialTab?: 'login' | 'register';
}>();

const emit = defineEmits<{
  close: [];
  authenticated: [];
}>();

const { $t, $ts, $localeRoute } = useI18n();
const { savePreferences, state } = useConsentState();
const grantMultipleConsents = useConvexMutation(api.users.consents.grantMultipleConsents);

const prefersReducedMotion = typeof window !== 'undefined'
  && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const loginSchema = toTypedSchema(
  z.object({
    email: z.string({ message: $ts('auth.validation.emailRequired') }).min(1, $ts('auth.validation.emailRequired')).email($ts('auth.validation.emailInvalid')),
    password: z.string({ message: $ts('auth.validation.passwordRequired') }).min(1, $ts('auth.validation.passwordRequired')),
  })
);

const registerSchema = toTypedSchema(
  z.object({
    email: z.string({ message: $ts('auth.validation.emailRequired') }).min(1, $ts('auth.validation.emailRequired')).email($ts('auth.validation.emailInvalid')),
    password: z.string({ message: $ts('auth.validation.passwordRequired') }).min(8, $ts('auth.validation.passwordMinLength')),
    confirmPassword: z.string({ message: $ts('auth.validation.confirmPasswordRequired') }).min(1, $ts('auth.validation.confirmPasswordRequired')),
    agreeTerms: z.literal(true, { message: $ts('auth.register.termsRequired') }),
  }).refine((data) => data.password === data.confirmPassword, {
    message: $ts('auth.validation.passwordsMismatch'),
    path: ['confirmPassword'],
  })
);

const registerInitialValues = {
  email: '',
  password: '',
  confirmPassword: '',
};

const authClient = useAuthClient();
const router = useRouter();
const { $refreshConvexAuth } = useNuxtApp();

useGoogleOneTap('/account');

const activeTab = ref<'login' | 'register'>('login');
const isLoading = ref(false);
const serverError = ref<string | null>(null);
const registerSuccess = ref(false);

const agreeTerms = ref(false);
const agreeMarketing = ref(true);
const agreeAnalytics = ref(true);

watch(activeTab, async (next) => {
  serverError.value = null;
  registerSuccess.value = false;
  await nextTick();
  moveTabIndicator(next);
  animateFormIn();
});

function setTab(next: 'login' | 'register') {
  if (activeTab.value === next) return;
  activeTab.value = next;
}

function moveTabIndicator(tab: 'login' | 'register') {
  if (!tabIndicatorRef.value || !tabsRef.value) return;
  const x = tab === 'login' ? 0 : (tabsRef.value.clientWidth / 2) - 4;
  gsap.to(tabIndicatorRef.value, {
    x,
    duration: prefersReducedMotion ? 0 : 0.4,
    ease: 'elastic.out(1, 0.75)',
  });
}

function animateFormIn() {
  if (!formsRef.value) return;
  const rows = formsRef.value.querySelectorAll('.auth-row');
  if (!rows.length) return;
  if (prefersReducedMotion) {
    gsap.set(rows, { opacity: 1, y: 0 });
    return;
  }
  gsap.fromTo(
    rows,
    { opacity: 0, y: 8 },
    { opacity: 1, y: 0, duration: 0.35, ease: 'power3.out', stagger: 0.04 }
  );
}

function onCheckPop(e: Event) {
  if (prefersReducedMotion) return;
  const target = (e?.target as HTMLElement | null)?.closest('label')?.querySelector('.auth-check');
  if (!target) return;
  gsap.fromTo(target, { scale: 0.8 }, { scale: 1, duration: 0.35, ease: 'back.out(3)' });
}

function friendlyError(msg: string | undefined, kind: 'email' | 'password' | 'confirmPassword'): string {
  if (!msg) return '';
  if (/expected string, received undefined/i.test(msg) || /required/i.test(msg)) {
    if (kind === 'email') return $ts('auth.validation.emailRequired');
    if (kind === 'password') return $ts('auth.validation.passwordRequired');
    if (kind === 'confirmPassword') return $ts('auth.validation.confirmPasswordRequired');
    return $ts('auth.validation.genericRequired');
  }
  return msg;
}

function onEnter(el: Element, done: () => void) {
  const card = (el as HTMLElement).querySelector('.auth-card');
  const backdrop = backdropRef.value;
  if (prefersReducedMotion) {
    if (backdrop) gsap.set(backdrop, { opacity: 1 });
    if (card) gsap.set(card, { opacity: 1, scale: 1, y: 0 });
    done();
    animateFormIn();
    nextTick(() => moveTabIndicator(activeTab.value));
    return;
  }
  const tl = gsap.timeline({
    onComplete: () => {
      done();
      animateFormIn();
      moveTabIndicator(activeTab.value);
    },
  });
  if (backdrop) tl.fromTo(backdrop, { opacity: 0 }, { opacity: 1, duration: 0.2, ease: 'power2.out' }, 0);
  if (card) {
    tl.fromTo(
      card,
      { opacity: 0, scale: 0.92, y: 24 },
      { opacity: 1, scale: 1, y: 0, duration: 0.5, ease: 'back.out(1.6)' },
      0
    );
  }
}

function onLeave(el: Element, done: () => void) {
  const card = (el as HTMLElement).querySelector('.auth-card');
  const backdrop = backdropRef.value;
  if (prefersReducedMotion) {
    done();
    return;
  }
  const tl = gsap.timeline({ onComplete: done });
  if (card) tl.to(card, { opacity: 0, scale: 0.95, y: 12, duration: 0.2, ease: 'power2.in' }, 0);
  if (backdrop) tl.to(backdrop, { opacity: 0, duration: 0.2, ease: 'power2.in' }, 0);
}

const handleEscape = (e: KeyboardEvent) => {
  if (e.key === 'Escape' && props.isOpen) {
    emit('close');
  }
};

onMounted(() => {
  document.addEventListener('keydown', handleEscape);
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleEscape);
  document.documentElement.style.overflow = '';
});

watch(() => props.isOpen, (isOpen) => {
  if (typeof document !== 'undefined') {
    // Lock on <html> (not <body>) so scrollbar-gutter: stable keeps the gutter reserved
    // and the page doesn't shift horizontally when the modal opens.
    document.documentElement.style.overflow = isOpen ? 'hidden' : '';
  }

  if (isOpen) {
    if (props.initialTab) {
      activeTab.value = props.initialTab;
    }
    nextTick(() => {
      panelRef.value?.focus();
      moveTabIndicator(activeTab.value);
    });
  } else {
    serverError.value = null;
    registerSuccess.value = false;
  }
});

async function handleLogin(values: Record<string, unknown>) {
  const email = values.email as string;
  const password = values.password as string;

  isLoading.value = true;
  serverError.value = null;

  try {
    const result = await authClient.signIn.email({ email, password });

    if (result.error) {
      serverError.value = result.error.message || 'Login failed. Please check your credentials.';
      return;
    }

    primeSessionCache(result);
    if (typeof $refreshConvexAuth === 'function') {
      $refreshConvexAuth();
    }

    emit('authenticated');
    emit('close');
    router.push('/account');
  } catch (e: unknown) {
    serverError.value = e instanceof Error ? e.message : 'An unexpected error occurred.';
  } finally {
    isLoading.value = false;
  }
}

async function handleRegister(values: Record<string, unknown>) {
  const email = values.email as string;
  const password = values.password as string;

  isLoading.value = true;
  serverError.value = null;

  try {
    const result = await authClient.signUp.email({
      name: email,
      email,
      password,
    });

    if (result.error) {
      serverError.value = result.error.message || 'Registration failed. Please try again.';
      return;
    }

    savePreferences({
      marketing: agreeMarketing.value,
      analytics: agreeAnalytics.value,
      third_party: false,
    });
    log.debug('Persisted registration consent choices');

    try {
      const policyVersion = state.value.policyVersion;
      await grantMultipleConsents({
        consents: [
          { consentType: 'terms', policyVersion },
          { consentType: 'privacy', policyVersion },
          { consentType: 'data_processing', policyVersion },
        ],
        source: 'registration',
      });
      log.debug('Recorded essential consents at registration');
    } catch (consentError) {
      log.warn('Failed to record essential consents', consentError);
    }

    registerSuccess.value = true;
  } catch (e: unknown) {
    serverError.value = e instanceof Error ? e.message : 'An unexpected error occurred.';
  } finally {
    isLoading.value = false;
  }
}

async function handleGoogleLogin() {
  isLoading.value = true;
  serverError.value = null;

  try {
    await authClient.signIn.social({
      provider: 'google',
      callbackURL: '/account',
    });
  } catch (e: unknown) {
    serverError.value = e instanceof Error ? e.message : 'Google login failed.';
    isLoading.value = false;
  }
}

async function handleAppleLogin() {
  isLoading.value = true;
  serverError.value = null;

  try {
    await authClient.signIn.social({
      provider: 'apple',
      callbackURL: '/account',
    });
  } catch (e: unknown) {
    serverError.value = e instanceof Error ? e.message : 'Apple login failed.';
    isLoading.value = false;
  }
}

async function handlePasskeyLogin() {
  isLoading.value = true;
  serverError.value = null;

  try {
    const result = await authClient.signIn.passkey();

    if (result?.error) {
      serverError.value = result.error.message || 'Passkey login failed.';
      return;
    }

    if (result) {
      primeSessionCache(result as Parameters<typeof primeSessionCache>[0]);
    }
    if (typeof $refreshConvexAuth === 'function') {
      $refreshConvexAuth();
    }

    emit('authenticated');
    emit('close');
    router.push('/account');
  } catch (e: unknown) {
    serverError.value = e instanceof Error ? e.message : 'Passkey login failed.';
  } finally {
    isLoading.value = false;
  }
}
</script>

<style scoped>
.auth-panel {
  will-change: transform, opacity;
}

/* Mobile: full-bleed sheet */
@media (max-width: 767px) {
  .auth-card {
    border-radius: 0 !important;
    box-shadow: none !important;
    border: none !important;
  }
}

/* Inputs — lighter, rounded, soft focus */
.auth-input-wrap :deep(.auth-input) {
  background-color: var(--muted);
  height: 3.25rem;
  border-radius: 1rem;
  border: 2px solid transparent;
  transition: background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease;
  font-weight: 500;
}

.auth-input-wrap :deep(.auth-input)::placeholder {
  color: var(--muted-foreground);
  font-weight: 400;
}

.auth-input-wrap :deep(.auth-input):hover {
  background-color: var(--accent);
}

/* Autofill — subtle ice-blue tint instead of the browser's harsh overlay */
.auth-input-wrap :deep(.auth-input):-webkit-autofill {
  -webkit-box-shadow: 0 0 0 1000px oklch(0.97 0.01 265) inset;
  box-shadow: 0 0 0 1000px oklch(0.97 0.01 265) inset;
  -webkit-text-fill-color: var(--foreground);
  transition: background-color 5000s ease-in-out 0s, border-color 0.2s ease, transform 0.15s ease;
}

.auth-input-wrap :deep(.auth-input):-webkit-autofill:hover {
  -webkit-box-shadow: 0 0 0 1000px oklch(0.96 0.01 265) inset;
  box-shadow: 0 0 0 1000px oklch(0.96 0.01 265) inset;
  -webkit-text-fill-color: var(--foreground);
}

.auth-input-wrap :deep(.auth-input):-webkit-autofill:focus {
  -webkit-box-shadow: 0 0 0 1000px var(--card) inset, 0 0 0 4px var(--shadow-color);
  box-shadow: 0 0 0 1000px var(--card) inset, 0 0 0 4px var(--shadow-color);
  -webkit-text-fill-color: var(--foreground);
  border-color: var(--foreground);
  transform: translateY(-1px);
}

.auth-input-wrap :deep(.auth-input):focus {
  background-color: var(--card);
  border-color: var(--foreground);
  box-shadow: 0 0 0 4px var(--shadow-color);
  transform: translateY(-1px);
}

.auth-input-wrap.is-invalid :deep(.auth-input) {
  border-color: var(--destructive);
  background-color: oklch(0.98 0.01 17);
  animation: auth-shake 0.35s cubic-bezier(.36,.07,.19,.97);
}

@keyframes auth-shake {
  10%, 90% { transform: translateX(-1px); }
  20%, 80% { transform: translateX(2px); }
  30%, 50%, 70% { transform: translateX(-4px); }
  40%, 60% { transform: translateX(4px); }
}

/* Primary CTA — Duolingo-style with bottom shadow pop */
.auth-cta {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  height: 3.25rem;
  padding: 0 1.25rem;
  border-radius: 1rem;
  background: var(--foreground);
  color: var(--background);
  font-weight: 700;
  font-size: 0.95rem;
  letter-spacing: 0.01em;
  box-shadow: 0 4px 0 0 var(--shadow-color-heavy);
  transition: transform 0.12s ease, box-shadow 0.12s ease, filter 0.2s ease;
  cursor: pointer;
}

.auth-cta:hover:not(:disabled) {
  filter: brightness(1.08);
}

.auth-cta:active:not(:disabled) {
  transform: translateY(3px);
  box-shadow: 0 1px 0 0 var(--shadow-color-heavy);
}

.auth-cta:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  box-shadow: 0 2px 0 0 var(--shadow-color-2xl);
}

/* Social / outline buttons with same Duolingo shadow */
.auth-social {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  height: 3rem;
  padding: 0 1rem;
  border-radius: 1rem;
  background: var(--card);
  color: var(--foreground);
  border: 2px solid var(--border);
  border-bottom-width: 4px;
  font-weight: 600;
  font-size: 0.9rem;
  transition: transform 0.12s ease, border-color 0.2s ease, background-color 0.2s ease;
  cursor: pointer;
}

.auth-social:hover:not(:disabled) {
  background-color: var(--accent);
  border-color: var(--input);
}

.auth-social:active:not(:disabled) {
  transform: translateY(2px);
  border-bottom-width: 2px;
}

.auth-social:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Checkbox pop feel */
.auth-check {
  transition: transform 0.15s ease;
}

/* Error message fade/slide */
.auth-error-enter-active,
.auth-error-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.auth-error-enter-from,
.auth-error-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
